import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiClient, saveSession, clearSession, hasSession, getApiErrorMessage } from "../lib/apiClient";
import { queryClient } from "../lib/queryClient";
import { setBusinessTimezone } from "../lib/utils";

const AuthContext = createContext(null);

// Cache the logged-in user so the app can still bootstrap into an authenticated
// state when offline (GET /auth/me fails without a response). Tokens are already
// preserved in that case; this keeps `user` populated so ProtectedRoutes don't
// bounce an offline driver to the login screen.
const CACHED_USER_KEY = "vt_cached_user";
function cacheUser(u) {
  try { localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u)); } catch { /* noop */ }
}
function readCachedUser() {
  try { return JSON.parse(localStorage.getItem(CACHED_USER_KEY) || "null"); } catch { return null; }
}
function clearCachedUser() {
  try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* noop */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!hasSession()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
      cacheUser(data);
    } catch (e) {
      // Only a genuine 401 (bad/expired token) means the session is truly
      // invalid. A network blip or transient 5xx must NOT wipe out saved
      // tokens, otherwise "Remember me" users get logged out by a hiccup.
      if (e?.response?.status === 401) {
        clearSession();
        clearCachedUser();
        setUser(null);
      } else if (!e?.response) {
        // Offline / no response: re-hydrate the last known user so an offline
        // deep-link reload stays authenticated and ProtectedRoutes render.
        const cached = readCachedUser();
        if (cached) setUser(cached);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
    // Business timezone is used by every portal (admin/driver/customer) for
    // "today" math — fetch once at app boot regardless of login state.
    apiClient.get("/outlet/timezone").then(({ data }) => setBusinessTimezone(data.timezone)).catch(() => {});
  }, [loadUser]);

  const login = async (username, password, remember = true) => {
    try {
      const { data } = await apiClient.post("/auth/login", { username, password });
      // Drop any cached query data from a previous account before switching.
      queryClient.clear();
      saveSession(data, remember);
      setUser(data.user);
      cacheUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      // A transient 502/503/504 (e.g. the DB/API waking up right after a
      // cold pod start) is not a credentials problem — silently retry once
      // before surfacing anything to the user, since it's usually gone
      // within a second or two.
      const status = error?.response?.status;
      if (status === 502 || status === 503 || status === 504) {
        try {
          await new Promise((r) => setTimeout(r, 1500));
          const { data } = await apiClient.post("/auth/login", { username, password });
          queryClient.clear();
          saveSession(data, remember);
          setUser(data.user);
          cacheUser(data.user);
          return { success: true, user: data.user };
        } catch (retryError) {
          // Fall through to the generic error message below using the retry's error.
          return { success: false, message: getApiErrorMessage(retryError, "Could not sign in. Please try again.") };
        }
      }
      // Reserve "Invalid username or password" for what the backend actually
      // says on a real 401 (returned via the string-detail branch above) —
      // any other unexpected failure must not be mislabeled as bad credentials.
      return { success: false, message: getApiErrorMessage(error, "Could not sign in. Please try again.") };
    }
  };

  const logout = useCallback(() => {
    clearSession();
    clearCachedUser();
    // Wipe all cached query data so no account's lists/counts can leak to the
    // next user (belt-and-suspenders; the redirect below also hard-reloads).
    queryClient.clear();
    setUser(null);
    const path = window.location.pathname;
    const loginPath = path.startsWith("/driver") ? "/driver/login"
      : path.startsWith("/customer") ? "/customer/login"
      : path.startsWith("/pos") ? "/pos/login"
      : "/login";
    window.location.href = loginPath;
  }, []);

  const hasPerm = useCallback((key) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    return (user.permissions || []).includes(key);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser: loadUser, hasPerm }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
